/**
 * Aura chip (#139) — the shared icon + duration + stack + border-tint tile for
 * a single aura instance. Built ONCE and reused by the self buff/debuff bar
 * (#139) and the nameplate/frame aura rows (#137/#138) — same visual,
 * different feed/filter (major-subset vs show-all).
 *
 * PURELY COSMETIC. Reads a `WireAura` (already wired via `WireEntity.auras`) and
 * the current tick; touches no sim/protocol/hash. The icon comes from the shared
 * `auraIcons.ts` pipeline; the buff/debuff border colour comes from the
 * WIRE-CARRIED `polarity` (#168, server-authored on the aura def) — an unmapped
 * aura falls back to a letter glyph (mirror of the ActionBar #72 fallback).
 *
 * TUNABLE-STYLE PATTERN (#48 combat text): every visual knob lives on
 * {@link AuraChipStyle}; nothing in the draw path is a magic number, so a future
 * settings UI / per-user theme swaps the style object with zero logic edits.
 *
 * #336: hovering a chip shows an {@link AuraTooltip} (name + authored
 * description — see `auraLabels.ts` — + remaining duration + stack count).
 * Added ONCE at this shared level (not duplicated per call site — `AuraChip`
 * is reused by the self bar AND the nameplate/frame major-aura rows, and both
 * inherit the hover behaviour for free). An unknown/undescribed def (no
 * authored `name`) shows NO tooltip — nothing legible to show. testid
 * `<testId>-tooltip`, only present in the DOM while hovered, mirroring the
 * ActionBar ability tooltip's (#339) `action-slot-<n>-tooltip` convention.
 *
 * data-* contract (component + e2e assertions):
 *   • data-testid       — `testId` prop (default "aura-chip")
 *   • data-aura-def     — the aura def id
 *   • data-kind         — "buff" | "debuff"
 *   • data-stacks       — stack count (>= 1)
 *   • data-permanent    — "true" when no expiry (no timer shown)
 *   • data-remaining    — whole seconds until expiry, or "" when permanent
 *   • data-icon         — "art" when a bundled icon resolved, else "glyph"
 */

import { useState, type CSSProperties, type ReactElement } from "react";
import type { WireAura } from "@pit/protocol";
import { ticksToSeconds } from "./hud-util.js";
import { auraIconGlyph, auraIconSrc } from "../champions/auraIcons.js";
import { auraDescription, auraLabel } from "../champions/auraLabels.js";
import { AuraTooltip } from "./AuraTooltip.js";

/** All visual treatment for an aura chip. One object, fully data-driven. */
export interface AuraChipStyle {
  /** Square chip edge length in px. */
  readonly sizePx: number;
  /** Border colour for a buff (WoW gold/neutral). */
  readonly buffBorderColor: string;
  /** Border colour for a debuff (WoW red). */
  readonly debuffBorderColor: string;
  /** Chip background. */
  readonly background: string;
  /** Letter-glyph fallback colour. */
  readonly glyphColor: string;
  /** Duration countdown text colour. */
  readonly timerColor: string;
  /** Stack-count badge text colour. */
  readonly stackColor: string;
}

/** Default treatment. The single source of truth for chip visuals. */
export const DEFAULT_AURA_CHIP_STYLE: AuraChipStyle = {
  sizePx: 36,
  buffBorderColor: "#c8a13a",
  debuffBorderColor: "#e04040",
  background: "#161c24",
  glyphColor: "#dfe6ef",
  timerColor: "#dfe6ef",
  stackColor: "#ffd873",
};

export interface AuraChipProps {
  readonly aura: WireAura;
  /** Current sim tick (for the `expiresAtTick − now` countdown). */
  readonly now: number;
  /** Ticks per second (countdown seconds = ceil(remaining / tickRate)). */
  readonly tickRate: number;
  readonly style?: AuraChipStyle;
  /** Overridable test id (default "aura-chip") so parents can namespace. */
  readonly testId?: string;
}

export function AuraChip({
  aura,
  now,
  tickRate,
  style = DEFAULT_AURA_CHIP_STYLE,
  testId = "aura-chip",
}: AuraChipProps): ReactElement {
  // #168: buff/debuff comes off the wire (server-authored polarity), not a
  // client mirror table.
  const kind = aura.polarity;
  const permanent = aura.expiresAtTick === null;
  const remainingSeconds =
    aura.expiresAtTick === null
      ? null
      : ticksToSeconds(aura.expiresAtTick, now, tickRate);
  const iconSrc = auraIconSrc(aura.def);
  const borderColor =
    kind === "buff" ? style.buffBorderColor : style.debuffBorderColor;
  // #336: hover tracking for the tooltip. Local UI state only — never touches
  // the store/sim, mirroring the ActionBar ability tooltip's (#339) pattern.
  const [hovered, setHovered] = useState(false);
  const name = auraLabel(aura.def);
  // No authored name ⇒ nothing legible to show; skip the tooltip entirely
  // rather than rendering an empty/def-id-only box.
  const showTooltip = hovered && name !== null;

  const root: CSSProperties = {
    position: "relative",
    width: style.sizePx,
    height: style.sizePx,
    background: style.background,
    border: `2px solid ${borderColor}`,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  return (
    // Outer wrapper: hover tracking + the tooltip's positioning anchor,
    // mirroring `ActionBar`'s `AbilityTooltip` pattern — kept SEPARATE from
    // the bordered box below (which clips icon art via `overflow: hidden`),
    // so the tooltip (which sits BELOW the chip, outside its bounds) never
    // gets clipped. `pointer-events: auto` re-enables hit-testing for this
    // wrapper specifically: chips commonly sit inside a `pointer-events:
    // none` row container (the self-aura-bar/major-aura-row wrappers are
    // click-through by design so they never block clicks on the game world
    // beneath them), so hover still works without making the whole row
    // interactive.
    <div
      style={{ position: "relative", pointerEvents: "auto" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showTooltip && name !== null ? (
        <AuraTooltip
          testId={`${testId}-tooltip`}
          name={name}
          description={auraDescription(aura.def)}
          remainingSeconds={remainingSeconds}
          stacks={aura.stacks}
        />
      ) : null}
      <div
        data-testid={testId}
        data-aura-def={aura.def}
        data-kind={kind}
        data-stacks={String(aura.stacks)}
        data-permanent={permanent ? "true" : "false"}
        data-remaining={remainingSeconds === null ? "" : String(remainingSeconds)}
        data-icon={iconSrc !== null ? "art" : "glyph"}
        style={root}
      >
        {iconSrc !== null ? (
          <img
            src={iconSrc}
            alt={aura.def}
            style={{
              position: "absolute",
              inset: 2,
              width: "calc(100% - 4px)",
              height: "calc(100% - 4px)",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div
            style={{
              fontSize: Math.round(style.sizePx * 0.5),
              fontWeight: 600,
              lineHeight: 1,
              color: style.glyphColor,
            }}
          >
            {auraIconGlyph(aura.def)}
          </div>
        )}
        {aura.stacks > 1 ? (
          <div
            data-testid={`${testId}-stacks`}
            style={{
              position: "absolute",
              right: 1,
              bottom: 0,
              zIndex: 2,
              fontSize: 11,
              fontWeight: 700,
              color: style.stackColor,
              textShadow: "0 0 2px #000",
              lineHeight: 1,
            }}
          >
            {String(aura.stacks)}
          </div>
        ) : null}
        {remainingSeconds !== null ? (
          <div
            data-testid={`${testId}-timer`}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 1,
              zIndex: 2,
              textAlign: "center",
              fontSize: 10,
              fontWeight: 600,
              color: style.timerColor,
              textShadow: "0 0 2px #000",
              lineHeight: 1,
            }}
          >
            {String(remainingSeconds)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
